import AppConstants from '../constants/appConstants';
import { get } from '../utils/fecth-wrappers';

const GROUP_ORDER = [
  'populares no brasil',
  'americanas',
  'asiáticas',
  'chinesas',
  'europeias',
  'premium e luxo',
  'outros',
  'outras',
];

/**
 * `name` de preferências que não devem ser tratadas como grupos de marcas ao agregar `value` (slugs).
 * Manter alinhado entre persistência (PreferencesFilters), hidratação e filtro de recomendados (usePreferences).
 */
export const BRAND_GROUP_TOKEN_EXCLUDED_NAMES = new Set([
  'BRANDS',
  'CAUTELAR_APROVADA',
  'ACCEPT_AUCTION',
  'ACCEPT_AUCTION_TYPE_A',
  'REJECT_AUCTION',
  'ENGINE_WITH_ISSUES',
  'TRANSMISSION_WITH_ISSUES',
  'ARMORED_AND_COMMON',
  'ONLY_ARMORED',
  'NO_ARMORED',
  'KM',
  'YEAR',
]);

export function isLikelyBrandGroupPreferenceEntry(p) {
  return (
    p &&
    typeof p === 'object' &&
    typeof p.name === 'string' &&
    Array.isArray(p.value) &&
    p.value.length > 0 &&
    !BRAND_GROUP_TOKEN_EXCLUDED_NAMES.has(p.name)
  );
}

/**
 * Lista todas as marcas (buyers v2), com `groups` para agrupamento na UI.
 */
export function fetchBuyersMakesAll() {
  const url = `${AppConstants.baseURLs.buyers}/makes/all`;
  return get(url)
    .then(data => {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.makes)) return data.makes;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    })
    .catch(() => []);
}

export function formatMakeDisplayName(slug) {
  if (!slug) return '';
  return String(slug)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function formatGroupTitle(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  return s
    .split(/\s+/)
    .map(
      word => word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1).toLocaleLowerCase('pt-BR')
    )
    .join(' ');
}

/** Agrupa por campo `groups` da API, com ordem estável de cards. */
export function groupBuyersMakesByGroup(makes) {
  const map = new Map();
  (makes || []).forEach(m => {
    const g = String(m.groups || '').trim() || 'outros';
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(m);
  });

  const keys = Array.from(map.keys());
  keys.sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a.toLowerCase());
    const ib = GROUP_ORDER.indexOf(b.toLowerCase());
    if (ia === -1 && ib === -1) return a.localeCompare(b, 'pt-BR');
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return keys.map(groupKey => ({
    groupKey,
    title: formatGroupTitle(groupKey),
    makes: map
      .get(groupKey)
      .sort((a, b) =>
        formatMakeDisplayName(a.name).localeCompare(formatMakeDisplayName(b.name), 'pt-BR')
      ),
  }));
}

/** Normaliza marca para comparar com `summary.make` / slug da API. */
export function normalizeMakeForFilterCompare(s) {
  if (s == null || s === '') return '';
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Hidrata seleção por grupo a partir do valor salvo em BRANDS.
 * Aceita:
 * - array legado (ids ou slugs)
 * - objeto legado `{ groups: [...] }`
 * - novo formato: array de grupos `{ name, isActive, value: [brands], _id }`
 */
/** `_id` vindo do Mongo (string ou `{ $oid }`) não é `groupKey` do card de marcas. */
function preferenceEntryIdToString(id) {
  if (id == null || id === '') return '';
  if (typeof id === 'object' && id.$oid != null) return String(id.$oid).trim();
  return String(id).trim();
}

export function parseBrandsPreferenceToSelectionsByGroup(value, brandGroups) {
  const empty = {};
  if (!value || !brandGroups?.length) return empty;

  if (Array.isArray(value)) {
    // Novo formato (array de objetos por grupo)
    if (
      value.length &&
      value.some(v => v && typeof v === 'object' && ('_id' in v || 'value' in v))
    ) {
      const groupKeyByNormalizedTitle = new Map(
        brandGroups.map(g => [
          String(g.title || '')
            .trim()
            .toLowerCase(),
          g.groupKey,
        ])
      );
      const byGroup = {};
      for (const g of value) {
        const nameKey = String(g?.name || '')
          .trim()
          .toLowerCase();
        let groupKey = groupKeyByNormalizedTitle.get(nameKey) || '';
        if (!groupKey) {
          const idStr = preferenceEntryIdToString(g?._id);
          if (idStr && brandGroups.some(bg => bg.groupKey === idStr)) {
            groupKey = idStr;
          }
        }
        if (!groupKey) continue;
        const brands = Array.isArray(g?.value) ? g.value : [];
        const slugSet = new Set(brands.map(v => String(v).toLowerCase()));
        const idSet = new Set(brands.map(v => String(v)));

        const group = brandGroups.find(bg => bg.groupKey === groupKey);
        if (!group) continue;
        const picks = group.makes
          .filter(
            m =>
              idSet.has(String(m._id)) ||
              slugSet.has(String(m.name).toLowerCase()) ||
              slugSet.has(normalizeMakeForFilterCompare(formatMakeDisplayName(m.name)))
          )
          .map(m => m._id);
        if (picks.length) byGroup[groupKey] = picks;
      }
      return byGroup;
    }

    const idSet = new Set(value.map(v => String(v)));
    const slugSet = new Set(value.map(v => String(v).toLowerCase()));
    const byGroup = {};
    for (const g of brandGroups) {
      const picks = g.makes
        .filter(
          m =>
            idSet.has(String(m._id)) ||
            slugSet.has(String(m.name).toLowerCase()) ||
            slugSet.has(normalizeMakeForFilterCompare(formatMakeDisplayName(m.name)))
        )
        .map(m => m._id);
      if (picks.length) byGroup[g.groupKey] = picks;
    }
    return byGroup;
  }

  if (typeof value === 'object' && Array.isArray(value.groups)) {
    const byGroup = {};
    for (const g of value.groups) {
      const key = g.groupKey;
      if (!key) continue;
      if (Array.isArray(g.makes) && g.makes.length) {
        byGroup[key] = g.makes.map(m => m.id ?? m._id).filter(Boolean);
      } else if (Array.isArray(g.makeIds) && g.makeIds.length) {
        byGroup[key] = [...g.makeIds];
      }
    }
    return byGroup;
  }

  return empty;
}

/** Seleção inicial na UI: todas as marcas marcadas por grupo (cadastro sem preferência de marcas salva). */
export function buildAllBrandsSelectedByGroup(brandGroups) {
  const byGroup = {};
  for (const g of brandGroups || []) {
    if (g.makes?.length) {
      byGroup[g.groupKey] = g.makes.map(m => m._id).filter(Boolean);
    }
  }
  return byGroup;
}

/**
 * Há dados de marcas já salvos na API (não aplicar default “todas ativas”).
 * `isLikelyBrandGroupPreferenceEntry` exige `value.length > 0`; aqui cobrimos legado e formatos vazios.
 */
export function hasPersistedBrandsPreference(legacyBrandsPref, brandGroupPrefsCount) {
  if (brandGroupPrefsCount > 0) return true;
  if (!legacyBrandsPref) return false;
  const v = legacyBrandsPref.value;
  if (v == null) return false;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    const isGroupShape = v.some(
      item => item && typeof item === 'object' && ('value' in item || '_id' in item)
    );
    if (isGroupShape) {
      return v.some(item => Array.isArray(item?.value) && item.value.length > 0);
    }
    return true;
  }
  if (typeof v === 'object' && Array.isArray(v.groups) && v.groups.length > 0) {
    return v.groups.some(
      g =>
        (Array.isArray(g.makes) && g.makes.length > 0) ||
        (Array.isArray(g.makeIds) && g.makeIds.length > 0)
    );
  }
  return false;
}

/**
 * Payload de preferências por grupo de marcas (um item por card na UI):
 * `{ name, isActive, value: [slugs] }`. O backend acrescenta `_id` (ObjectId do item), não confundir com `groupKey`: o parse resolve o grupo pelo `name` (título do card), e só usa `_id` se for igual a algum `groupKey`.
 *
 * `value` usa slugs (`m.name`) para bater com `summary.make`.
 */
export function buildBrandsPreferenceGroupsPayload(brandSelectionsByGroup, brandGroups) {
  const byGroupKey = new Map((brandGroups || []).map(g => [g.groupKey, g]));

  // Sempre inclui TODOS os grupos (mesma ordem da UI).
  return (brandGroups || []).map(group => {
    const groupKey = group.groupKey;
    const makeIds = brandSelectionsByGroup?.[groupKey] || [];
    const byId = new Map((byGroupKey.get(groupKey)?.makes || []).map(m => [String(m._id), m]));
    const slugs = (makeIds || []).map(id => byId.get(String(id))?.name).filter(Boolean);

    return {
      name: group?.title || formatGroupTitle(groupKey),
      isActive: slugs.length > 0,
      value: slugs,
    };
  });
}

/** Tokens (slug / id) usados pelo filtro de recomendados a partir de BRANDS.value. */
export function collectBrandFilterTokensFromPreferenceValue(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    // Novo formato: array de grupos
    if (
      value.length &&
      value.some(v => v && typeof v === 'object' && ('_id' in v || 'value' in v))
    ) {
      const tokens = [];
      for (const g of value) {
        const brands = Array.isArray(g?.value) ? g.value : [];
        tokens.push(...brands.map(String));
      }
      return tokens.filter(v => v != null && v !== '');
    }
    // Legado: array simples
    return value.filter(v => v != null && v !== '').map(String);
  }
  if (typeof value === 'object' && Array.isArray(value.groups)) {
    const tokens = [];
    for (const g of value.groups) {
      if (Array.isArray(g.makes)) {
        for (const m of g.makes) {
          if (m?.name) tokens.push(String(m.name));
          else if (m?.id != null) tokens.push(String(m.id));
          else if (m?._id != null) tokens.push(String(m._id));
        }
      }
      if (Array.isArray(g.makeIds)) tokens.push(...g.makeIds.map(String));
    }
    return tokens;
  }
  return [];
}
